import { analyticsService, onboardingManager } from "@core/service-container";
import { getDayColumnIndexes, ProgramContent } from "@domain/program/program";
import { DayIndex } from "@domain/program/program-days";
import { OnboardingProgramQuickstartStep } from "@domain/onboarding/onboarding";
import { DialogContext, ModalId } from "@ui/components/dialog/dialog-provider";
import { TrackingContext } from "@ui/components/common/tracking-context-provider";
import { DayColumnState } from "@ui/pages/program-builder/dialog/duplicate-day-column-dialog";
import { ExerciseDragAndDropContext } from "@ui/pages/program-builder/drag-drop/program-builder-drag-drop-context";
import { isNotNil } from "@utils/assert";
import { useCallback, useContext } from "react";

interface UseEditorActions {
	openDuplicateColumnModal: (sourceDayIndex: DayIndex) => void;
	onClearSession: (dayIndex: DayIndex) => void;
}

export const useEditorActions = (editedProgram: ProgramContent): UseEditorActions => {
	const dialogContext = useContext(DialogContext);
	const exerciseDragAndDropContext = useContext(ExerciseDragAndDropContext);
	const trackingContext = useContext(TrackingContext);

	const openDuplicateColumnModal = useCallback(
		(sourceDayIndex: DayIndex) =>
			dialogContext?.openModal(ModalId.DuplicateDayColumn, {
				initialDayColumns: new Map(
					getDayColumnIndexes().map(dayIndex => {
						let columnState = DayColumnState.AVAILABLE;
						if (sourceDayIndex === dayIndex) {
							columnState = DayColumnState.SOURCE;
						} else {
							const session = editedProgram.days.get(dayIndex);
							if (isNotNil(session) && session.exercises.length > 0) {
								columnState = DayColumnState.NOT_AVAILABLE;
							}
						}
						return [dayIndex, columnState];
					}),
				),
				onConfirm: (sourceSessionIndex, destinationSessionIndexes) => {
					destinationSessionIndexes.forEach(destinationSessionIndex =>
						exerciseDragAndDropContext?.programEditor.duplicateSession(sourceSessionIndex, destinationSessionIndex),
					);
					analyticsService.trackEvent({
						category: trackingContext.category,
						action: "duplicateSession",
						params: [destinationSessionIndexes.length],
					});
					if (destinationSessionIndexes.length > 0) {
						void onboardingManager.passStepOnboarding(
							OnboardingProgramQuickstartStep.DUPLICATE_SESSION_GUIDE_02,
						);
					}
				},
			}),
		[dialogContext, editedProgram.days, exerciseDragAndDropContext?.programEditor, trackingContext.category],
	);

	const onClearSession = useCallback(
		(dayIndex: DayIndex) => {
			exerciseDragAndDropContext?.programEditor.clearSession(dayIndex);
			analyticsService.trackEvent({
				category: trackingContext.category,
				action: "clearSession",
				params: [],
			});
		},
		[exerciseDragAndDropContext?.programEditor, trackingContext.category],
	);

	return { openDuplicateColumnModal, onClearSession };
};
